import { Link, useParams } from 'react-router';
import type { ProjectDto } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  CARE_PLAN_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  PROJECT_STATUS_LABELS,
  outstandingInr,
  useClient,
  waClientHref,
} from '../lib/clients.js';
import { formatDate, formatDateTime, formatInr } from '../lib/format.js';
import { useCreateCarePlan } from '../lib/billing.js';
import { useCan } from '../lib/permissions.js';

/* ============================================================================
   /clients/:id — one client, and everything they have with us.
   ----------------------------------------------------------------------------
   Read-only. Nothing here writes, because nothing in the API writes a client
   or a project yet — see CONTEXT.md §12. Showing a disabled edit button would
   be a promise the backend cannot keep.
   ========================================================================== */

/** The outward links a project has, in the order they become useful. */
function projectLinks(project: ProjectDto): { href: string; label: string }[] {
  return [
    project.previewUrl ? { href: project.previewUrl, label: 'Preview' } : null,
    project.liveUrl ? { href: project.liveUrl, label: 'Live site' } : null,
  ].filter((link) => link !== null);
}

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const { data, isPending, error } = useClient(id);

  if (isPending) return <p className="muted">Loading…</p>;

  if (error) {
    return (
      <>
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'That client was not found.'}
        </p>
        <Link to="/clients" className="btn btn--ghost">
          Back to clients
        </Link>
      </>
    );
  }

  const { client, projects, invoices, carePlans } = data;
  const outstanding = outstandingInr(invoices);
  /* A cancelled plan does not count: that is how someone resubscribes. The API
     enforces the same rule and answers 409, so this only decides what to show. */
  const hasLivePlan = carePlans.some((plan) => plan.status !== 'cancelled');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{client.business}</h1>
          <p className="muted">
            {client.ownerName} · client since {formatDate(client.createdAt)}
          </p>
        </div>
        <a className="btn" href={waClientHref(client)} target="_blank" rel="noreferrer">
          Message on WhatsApp
        </a>
      </div>

      <div className="stack">
        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Details</h2>
          <dl className="detail-grid">
            <div>
              <dt>Owner</dt>
              <dd>{client.ownerName}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>
                <a href={`tel:${client.phone}`}>{client.phone}</a>
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${client.email}`}>{client.email}</a>
              </dd>
            </div>
            <div>
              <dt>GSTIN</dt>
              <dd>{client.gstin ?? '—'}</dd>
            </div>
            <div>
              {/* "Outstanding", not "Due": an unpaid invoice below is labelled
                  "Due", and one screen using one word for both a total and a
                  status makes the reader work out which is which. */}
              <dt>Outstanding</dt>
              {/* The sum of unpaid invoices, not of every invoice. */}
              <dd>{outstanding > 0 ? formatInr(outstanding) : '—'}</dd>
            </div>
            <div>
              <dt>Came from</dt>
              <dd>
                {client.leadId ? (
                  <Link to={`/leads/${client.leadId}`}>Original lead</Link>
                ) : (
                  'Came in directly'
                )}
              </dd>
            </div>
          </dl>
        </section>

        <section className="card">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18 }}>Projects</h2>
            <Link to={`/projects/new?clientId=${client.id}`} className="btn btn--ghost btn--small">
              New project
            </Link>
          </div>
          {projects.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No projects yet.
            </p>
          ) : (
            <div className="stack">
              {projects.map((project) => (
                <div className="note" key={project.id}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <Link to={`/projects/${project.id}`} style={{ fontWeight: 700 }}>
                      {project.plan} · {formatInr(project.priceInr)}
                    </Link>
                    <div className="row" style={{ gap: 6 }}>
                      {project.overdue ? <span className="pill pill--warn">Late</span> : null}
                      <span className="pill pill--muted">
                        {PROJECT_STATUS_LABELS[project.status]}
                      </span>
                    </div>
                  </div>

                  <p className="note__meta" style={{ margin: '6px 0 0' }}>
                    {project.promisedDate
                      ? `Promised ${formatDate(project.promisedDate)}`
                      : 'No date promised'}
                    {project.launchedAt ? ` · live ${formatDate(project.launchedAt)}` : ''}
                    {project.domain ? ` · ${project.domain}` : ''}
                  </p>

                  {/* Built as a list rather than two conditionals inside a
                      wrapper: the wrapper exists only for spacing, and a
                      `previewUrl ?? liveUrl` test would hide a real live link
                      whenever previewUrl was an empty string rather than null. */}
                  {projectLinks(project).length > 0 ? (
                    <div className="row" style={{ marginTop: 8, gap: 6 }}>
                      {projectLinks(project).map((link) => (
                        <a
                          key={link.label}
                          className="btn btn--ghost btn--small"
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Invoices</h2>
          {invoices.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No invoices yet.
            </p>
          ) : (
            <div className="stack">
              {invoices.map((invoice) => (
                <div className="note" key={invoice.id}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <strong>{invoice.number}</strong>
                    <div className="row" style={{ gap: 6 }}>
                      <span>{formatInr(invoice.amountInr)}</span>
                      <span
                        className={invoice.status === 'due' ? 'pill pill--warn' : 'pill'}
                        /* 'due' is the only one that needs acting on, so it is
                           the only one that gets the loud colour. */
                      >
                        {INVOICE_STATUS_LABELS[invoice.status]}
                      </span>
                    </div>
                  </div>
                  <p className="note__meta" style={{ margin: '6px 0 0' }}>
                    {invoice.type} · {formatInr(invoice.amountInr)} + {invoice.gstPercent}% GST
                    {invoice.paidAt ? ` · paid ${formatDateTime(invoice.paidAt)}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18 }}>Care plans</h2>
            <StartCarePlan clientId={client.id} hasLivePlan={hasLivePlan} />
          </div>
          {carePlans.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No care plan.
            </p>
          ) : (
            <div className="stack">
              {carePlans.map((plan) => (
                <div className="note" key={plan.id}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <strong>{formatInr(plan.monthlyInr)}/month</strong>
                    <span className={plan.status === 'active' ? 'pill' : 'pill pill--muted'}>
                      {CARE_PLAN_STATUS_LABELS[plan.status]}
                    </span>
                  </div>
                  <p className="note__meta" style={{ margin: '6px 0 0' }}>
                    {plan.status === 'active' && plan.nextBillingAt
                      ? `Next billing ${formatDate(plan.nextBillingAt)}`
                      : plan.cancelledAt
                        ? `Cancelled ${formatDate(plan.cancelledAt)}`
                        : 'No billing date'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <Link to="/clients" className="btn btn--ghost">
          Back to clients
        </Link>
      </div>
    </>
  );
}

/**
 * Start the ₹999 plan. Owner-only, and hidden while one is already live.
 *
 * No form: the price is the list price and the billing date belongs to the
 * job. Somewhere to change the amount would be a field nobody uses on the one
 * screen where a wrong number bills a client every month.
 */
function StartCarePlan({ clientId, hasLivePlan }: { clientId: string; hasLivePlan: boolean }) {
  const may = useCan();
  const create = useCreateCarePlan();

  if (!may('care-plans:create') || hasLivePlan) return null;

  return (
    <div className="row" style={{ gap: 6 }}>
      <button
        type="button"
        className="btn btn--ghost btn--small"
        disabled={create.isPending}
        onClick={() => create.mutate({ clientId, monthlyInr: 999 })}
      >
        Start a care plan
      </button>
      {create.isError ? (
        <span className="field__error" role="alert">
          {create.error instanceof ApiError ? create.error.message : 'Could not start it.'}
        </span>
      ) : null}
    </div>
  );
}
